import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { contactApiKeyStore } from '../../../api/contact-api-key-store';

const styling = {
  dialogTitleStyle: { padding: '12px 24px 4px' },
  itemStyle: {
    padding: '8px',
    margin: '5px 0',
    borderRadius: 5,
    border: '1px solid #D5D8DC',
  },
  newItemStyle: {
    padding: '8px',
    margin: '5px 0',
    borderRadius: 5,
    border: '1px solid #3498DB',
  },
};

interface MappingOfficeCell {
  officeData: any;
}

export const MappingOfficeCell = ({ officeData }: MappingOfficeCell) => {
  return (
    <Paper elevation={0} style={officeData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {officeData?.officeName}
          </Typography>
        </Grid>
        {officeData?.officeMapping && (
          <Grid item>
            <Tooltip title={<Typography variant="body1">Mapped</Typography>}>
              <CheckCircleIcon style={{ color: '#28B463' }} />
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

interface MappingProjectCell {
  projectData: any;
  officeName: string;
}

export const MappingProjectCell = ({ projectData, officeName }: MappingProjectCell) => {
  return (
    <Paper elevation={0} style={projectData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {projectData?.projectName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Office: {officeName}
          </Typography>
        </Grid>
        {projectData?.projectMapping && (
          <Grid item>
            <Tooltip title={<Typography variant="body1">Mapped</Typography>}>
              <CheckCircleIcon style={{ color: '#28B463' }} />
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

interface MappingUnitCell {
  unitData: any;
  officeName: string;
  projectName: string;
}
export const MappingUnitCell = ({ unitData, projectName, officeName }: MappingUnitCell) => {
  return (
    <Paper elevation={0} style={unitData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {unitData?.unitName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Office: {officeName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Project: {projectName}
          </Typography>
        </Grid>
        {unitData?.unitMapping && (
          <Grid item>
            <Tooltip title={<Typography variant="body1">Mapped</Typography>}>
              <CheckCircleIcon style={{ color: '#28B463' }} />
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

interface ViewMapContactDialog {
  contactID: any;
  onClosed: () => void;
}
export const ViewMapContactDialog = ({ contactID, onClosed }: ViewMapContactDialog) => {
  const [mappedData, setMappedData] = useState<any>([]);
  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
    contactApiKeyStore.getMappingContacts(contactID),
    !!contactID && contactID > 0
  );

  useEffect(() => {
    if (data) {
      setMappedData(data?.data?.contactsOfficeMappings);
    }
  }, [data]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'lg'}
      //  onClose={onClosed}
    >
      <DialogTitle id="form-dialog-title" style={styling.dialogTitleStyle}>
        View Mapping{' '}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <Grid container spacing={2}>
          <Grid item xl={12} lg={12} sm={12} xs={12}>
            {!isLoading || !isFetching ? (
              mappedData?.length > 0 ? (
                mappedData?.map((officeMapData: any, index: any) => (
                  <Grid container key={`${officeMapData?.officeName}-${officeMapData?.officeID}`}>
                    <Grid item xl={12} lg={12} sm={12} xs={12}>
                      <MappingOfficeCell officeData={officeMapData} />
                      {officeMapData?.contactsProjectMappings?.map((projectMapData: any) => (
                        <Grid
                          key={`${projectMapData?.projectName}-${projectMapData?.projectID}`}
                          container
                          style={{ paddingLeft: '60px' }}
                        >
                          <Grid item xl={12} lg={12} sm={12} xs={12}>
                            <MappingProjectCell projectData={projectMapData} officeName={officeMapData?.officeName} />
                            {projectMapData?.contactsUnitMappings?.map((unitMapData: any) => (
                              <Grid
                                key={`${unitMapData?.unitName}-${unitMapData?.unitID}`}
                                container
                                style={{ paddingLeft: '100px' }}
                              >
                                <Grid item xl={12} lg={12} sm={12} xs={12}>
                                  <MappingUnitCell
                                    unitData={unitMapData}
                                    projectName={projectMapData?.projectName}
                                    officeName={officeMapData?.officeName}
                                  />
                                </Grid>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ))
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 25 }}>
                  <p style={{ textAlign: 'center' }}>
                    <b>No data available right now.</b>
                  </p>
                </div>
              )
            ) : (
              <>
                <Skeleton variant="rounded" height={40} style={{ marginBottom: 10 }} />
                <Skeleton variant="rounded" height={40} style={{ marginBottom: 10 }} />
                <Skeleton variant="rounded" height={40} />
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
